import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  getProductsCatalog,
  getCatalogFacets,
  normalizePaginatedItems,
  CATALOG_PAGE_SIZE,
  catalogItemKey,
  persistFlowSelectedProduct,
} from '../apiClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Filter, ChevronDown } from 'lucide-react';

// Canonical category names to fix duplicates like T-Shirt vs T-shirt
const CANONICAL_TYPES = {
  't-shirt': 'T-Shirt',
  'hoodie': 'Hoodie',
  'sweatshirt': 'Sweatshirt',
  'tank top': 'Tank Top',
  'mug': 'Mug',
  'poster': 'Poster',
  'hat': 'Hat',
  'bag': 'Bag',
};

const getProductType = (productName) => {
  if (typeof productName !== 'string') return 'Other';
  const match = productName.match(new RegExp("(T-Shirt|Hoodie|Sweatshirt|Tank Top|Mug|Poster|Hat|Bag)", "i"));
  if (match) {
    const key = match[1].toLowerCase();
    return CANONICAL_TYPES[key] || match[1];
  }
  return 'Other';
};

/** Default category on first visit — matches server default filter. */
const DEFAULT_CATEGORY = 'T-Shirt';

/** Sidebar categories: explicit list (no \"Other\" bucket). Extras still merge in from API/catalog. */
const CATALOG_CATEGORY_LABELS = ['Bag', 'Hat', 'Hoodie', 'Mug', 'Poster', 'Sweatshirt', 'T-Shirt', 'Tank Top'];

const REGION_KEYWORDS = {
  US: ['United States', 'USA', 'U.S.'],
  GB: ['United Kingdom', 'UK', 'Great Britain'],
  SA: ['Saudi Arabia', 'KSA'],
  AE: ['United Arab Emirates', 'UAE'],
  CA: ['Canada'],
  AU: ['Australia'],
  DE: ['Germany'],
  FR: ['France'],
  ES: ['Spain'],
  IT: ['Italy'],
  NL: ['Netherlands'],
  BE: ['Belgium'],
  PL: ['Poland'],
  IN: ['India'],
  JP: ['Japan'],
  BR: ['Brazil'],
  MX: ['Mexico'],
  EG: ['Egypt'],
  KW: ['Kuwait'],
  QA: ['Qatar'],
  BH: ['Bahrain'],
  OM: ['Oman'],
  JO: ['Jordan'],
};

function getBrowserRegionCandidates() {
  const out = [];
  const pushKw = (tag) => {
    if (!tag) return;
    const list = REGION_KEYWORDS[tag];
    if (list) for (const k of list) if (!out.includes(k)) out.push(k);
  };
  try {
    if (typeof navigator !== 'undefined' && navigator.language) {
      pushKw(navigator.language.split('-')[1]?.toUpperCase());
    }
    const loc = Intl.DateTimeFormat().resolvedOptions().locale || '';
    pushKw(loc.split('-')[1]?.toUpperCase());
  } catch {
    /* ignore */
  }
  return out;
}

function collectShippingLabelsFromCatalog(catalog) {
  const labels = new Set();
  catalog.forEach((product) => {
    const locs = product.coverage_locations;
    if (locs && locs.length > 0) locs.forEach((l) => labels.add(l));
    else labels.add('Worldwide');
  });
  return labels;
}

function findMatchingShippingOption(labelsSet, candidates) {
  const options = [...labelsSet].filter((o) => o && o !== 'All');
  const norm = (s) => String(s).toLowerCase().replace(/\s+/g, ' ').trim();
  for (const cand of candidates) {
    const cn = norm(cand);
    for (const opt of options) {
      const on = norm(opt);
      if (!on || !cn) continue;
      if (on.includes(cn) || cn.includes(on)) return opt;
    }
  }
  return null;
}

/** Active catalog only (API defaults discontinued=false); still guard stale rows. */
function catalogProductIsSelectable(product) {
  if (!product || product.is_discontinued) return false;
  const st = product.availability_statuses;
  if (!st || st.length === 0) return true;
  return st.some((s) => s === 'in_stock');
}

const getAvailabilityStatus = (statuses) => {
  if (!statuses || statuses.length === 0) {
    return <span className="text-yellow-500">Unknown</span>;
  }
  const allInStock = statuses.every(s => s === 'in_stock');
  if (allInStock) {
    return <span className="text-green-500">In Stock</span>;
  }
  const allOutOfStock = statuses.every(s => s === 'out_of_stock');
  if (allOutOfStock) {
    return <span className="text-red-500">Out of Stock</span>;
  }
  return <span className="text-orange-500">Mixed Stock</span>;
};

const SEARCH_DEBOUNCE_MS = 320;

const ProductCatalogPage = () => {
  const location = useLocation();
  const { selectedImage } = location.state || {};
  const [productsCatalog, setProductsCatalog] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedProductType, setSelectedProductType] = useState(DEFAULT_CATEGORY);
  const [selectedShipping, setSelectedShipping] = useState('');
  const [selectedPrintLocation, setSelectedPrintLocation] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState('');
  const [shipsToOpen, setShipsToOpen] = useState(false);
  const [shipsToQuery, setShipsToQuery] = useState('');
  /** undefined = loading, null = failed, object = facets payload */
  const [facets, setFacets] = useState(undefined);
  const navigate = useNavigate();
  const shippingAutoAppliedRef = useRef(false);
  const userPickedShippingRef = useRef(false);
  const categoryWrapRef = useRef(null);
  const categorySearchInputRef = useRef(null);
  const shipsToWrapRef = useRef(null);
  const shipsToSearchInputRef = useRef(null);
  const [printLocOpen, setPrintLocOpen] = useState(false);
  const [printLocQuery, setPrintLocQuery] = useState('');
  const printLocWrapRef = useRef(null);
  const printLocSearchInputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getCatalogFacets({});
        if (!cancelled) setFacets(data);
      } catch (e) {
        console.error('Error fetching catalog facets:', e);
        if (!cancelled) setFacets(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const catalogQueryParams = useCallback(
    (offset) => {
      const params = {
        limit: CATALOG_PAGE_SIZE,
        offset,
      };
      const q = debouncedSearch.trim();
      if (q) params.q = q;
      if (selectedShipping) params.ships_to = selectedShipping;
      if (selectedPrintLocation) params.print_placement = selectedPrintLocation;
      if (selectedProductType) params.category = selectedProductType;
      return params;
    },
    [debouncedSearch, selectedProductType, selectedShipping, selectedPrintLocation],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getProductsCatalog(catalogQueryParams(0));
        if (cancelled) return;
        setProductsCatalog(normalizePaginatedItems(data));
        setCatalogTotal(typeof data?.total === 'number' ? data.total : normalizePaginatedItems(data).length);
      } catch (err) {
        console.error('Error fetching products catalog:', err);
        if (!cancelled) {
          setError('Failed to load products. Please try again later.');
          setProductsCatalog([]);
          setCatalogTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [catalogQueryParams]);

  useEffect(() => {
    if (shippingAutoAppliedRef.current || userPickedShippingRef.current) return;
    let labels = null;
    if (facets && Array.isArray(facets.shipping_options)) {
      if (facets.shipping_options.length === 0) {
        shippingAutoAppliedRef.current = true;
        return;
      }
      labels = new Set(facets.shipping_options);
    } else if (facets === null && productsCatalog.length > 0) {
      labels = collectShippingLabelsFromCatalog(productsCatalog);
    }
    if (!labels || labels.size === 0) return;
    const candidates = getBrowserRegionCandidates();
    const matched = findMatchingShippingOption(labels, candidates);
    if (matched) setSelectedShipping(matched);
    else if (labels.has('Worldwide')) setSelectedShipping('Worldwide');
    shippingAutoAppliedRef.current = true;
  }, [facets, productsCatalog]);

  const loadMoreCatalog = async () => {
    if (loadingMore || productsCatalog.length >= catalogTotal) return;
    try {
      setLoadingMore(true);
      const offset = productsCatalog.length;
      const data = await getProductsCatalog(catalogQueryParams(offset));
      const batch = normalizePaginatedItems(data);
      if (typeof data?.total === 'number') setCatalogTotal(data.total);
      setProductsCatalog((prev) => {
        const seen = new Set(prev.map((p) => catalogItemKey(p)));
        const merged = [...prev];
        for (const p of batch) {
          const k = catalogItemKey(p);
          if (p && k && !seen.has(k)) {
            seen.add(k);
            merged.push(p);
          }
        }
        return merged;
      });
    } catch (err) {
      console.error('Error loading more products:', err);
      setError('Failed to load more products.');
    } finally {
      setLoadingMore(false);
    }
  };

  const productTypes = useMemo(() => {
    const labels = new Set(CATALOG_CATEGORY_LABELS);
    if (facets && Array.isArray(facets.product_types)) {
      facets.product_types.forEach((t) => {
        if (t && t !== 'Other') labels.add(t);
      });
    }
    productsCatalog.forEach((product) => {
      const t = getProductType(product.name);
      if (t && t !== 'Other') labels.add(t);
    });
    const sorted = [...labels].sort((a, b) => a.localeCompare(b));
    return ['All', ...sorted];
  }, [facets, productsCatalog]);

  const filteredCategoryOptions = useMemo(() => {
    const q = categoryQuery.trim().toLowerCase();
    if (!q) return productTypes;
    return productTypes.filter((t) => String(t).toLowerCase().includes(q));
  }, [productTypes, categoryQuery]);

  const shippingOptions = useMemo(() => {
    if (facets && Array.isArray(facets.shipping_options) && facets.shipping_options.length > 0) {
      const rest = [...facets.shipping_options].sort((a, b) =>
        (a === 'Worldwide' ? -1 : b === 'Worldwide' ? 1 : a.localeCompare(b)));
      return ['All', ...rest];
    }
    const set = new Set();
    productsCatalog.forEach((product) => {
      const locs = product.coverage_locations;
      if (locs && locs.length > 0) locs.forEach((l) => set.add(l));
      else set.add('Worldwide');
    });
    return ['All', ...Array.from(set).sort((a, b) => (a === 'Worldwide' ? -1 : b === 'Worldwide' ? 1 : a.localeCompare(b)))];
  }, [facets, productsCatalog]);

  const filteredShippingOptions = useMemo(() => {
    const q = shipsToQuery.trim().toLowerCase();
    if (!q) return shippingOptions;
    return shippingOptions.filter((loc) => String(loc).toLowerCase().includes(q));
  }, [shippingOptions, shipsToQuery]);

  useEffect(() => {
    if (!shipsToOpen) return;
    const onPointerDown = (e) => {
      if (shipsToWrapRef.current && !shipsToWrapRef.current.contains(e.target)) {
        setShipsToOpen(false);
        setShipsToQuery('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [shipsToOpen]);

  useEffect(() => {
    if (!shipsToOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setShipsToOpen(false);
        setShipsToQuery('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shipsToOpen]);

  useEffect(() => {
    if (shipsToOpen && shipsToSearchInputRef.current) {
      shipsToSearchInputRef.current.focus();
    }
  }, [shipsToOpen]);

  useEffect(() => {
    if (!categoryOpen) return;
    const onPointerDown = (e) => {
      if (categoryWrapRef.current && !categoryWrapRef.current.contains(e.target)) {
        setCategoryOpen(false);
        setCategoryQuery('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [categoryOpen]);

  useEffect(() => {
    if (!categoryOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setCategoryOpen(false);
        setCategoryQuery('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [categoryOpen]);

  useEffect(() => {
    if (categoryOpen && categorySearchInputRef.current) {
      categorySearchInputRef.current.focus();
    }
  }, [categoryOpen]);

  useEffect(() => {
    if (!printLocOpen) return;
    const onPointerDown = (e) => {
      if (printLocWrapRef.current && !printLocWrapRef.current.contains(e.target)) {
        setPrintLocOpen(false);
        setPrintLocQuery('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [printLocOpen]);

  useEffect(() => {
    if (!printLocOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setPrintLocOpen(false);
        setPrintLocQuery('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [printLocOpen]);

  useEffect(() => {
    if (printLocOpen && printLocSearchInputRef.current) {
      printLocSearchInputRef.current.focus();
    }
  }, [printLocOpen]);

  const printLocationOptions = useMemo(() => {
    if (facets && Array.isArray(facets.print_placements) && facets.print_placements.length > 0) {
      const opts = facets.print_placements.map((p) => ({ type: p.type, title: p.title || p.type }));
      opts.sort((a, b) => a.title.localeCompare(b.title));
      return [{ type: '', title: 'All' }, ...opts];
    }
    const byType = new Map();
    productsCatalog.forEach((product) => {
      const placements = product.supported_print_placements || [];
      placements.forEach((p) => {
        if (p.type && !byType.has(p.type)) byType.set(p.type, p.title || p.type);
      });
    });
    return [{ type: '', title: 'All' }, ...Array.from(byType.entries()).map(([type, title]) => ({ type, title })).sort((a, b) => a.title.localeCompare(b.title))];
  }, [facets, productsCatalog]);

  const filteredPrintLocationOptions = useMemo(() => {
    const q = printLocQuery.trim().toLowerCase();
    if (!q) return printLocationOptions;
    return printLocationOptions.filter(
      (o) =>
        String(o.title || '')
          .toLowerCase()
          .includes(q) || String(o.type || '').toLowerCase().includes(q),
    );
  }, [printLocationOptions, printLocQuery]);

  const selectedPrintLocationLabel = useMemo(() => {
    if (!selectedPrintLocation) return 'All print locations';
    const o = printLocationOptions.find((x) => x.type === selectedPrintLocation);
    return o?.title || selectedPrintLocation;
  }, [selectedPrintLocation, printLocationOptions]);

  const handleProductSelect = async (product) => {
    if (selectedImage) {
      navigate('/configure-mockup', { state: { selectedProduct: product, selectedImage: selectedImage } });
    } else {
      persistFlowSelectedProduct(product);
      navigate('/designs', { state: { selectedProduct: product } });
    }
  };

  const clearFilter = () => setSelectedProductType('');
  const clearAllFilters = () => {
    userPickedShippingRef.current = true;
    setSelectedProductType('');
    setSelectedShipping('');
    setSelectedPrintLocation('');
    setSearchTerm('');
    setShipsToOpen(false);
    setShipsToQuery('');
    setCategoryOpen(false);
    setCategoryQuery('');
    setPrintLocOpen(false);
    setPrintLocQuery('');
  };
  const hasActiveFilters =
    !!debouncedSearch.trim() ||
    selectedProductType ||
    (selectedShipping && selectedShipping !== 'All') ||
    !!selectedPrintLocation;

  const designBannerThumb = selectedImage
    ? (selectedImage.mockup_image_url || selectedImage.image_url || selectedImage.url)
    : null;
  const designBannerTitle = selectedImage?.prompt || selectedImage?.name || 'Your design';

  return (
    <div className="container mx-auto p-4 bg-background rounded-lg shadow-lg tour-products min-h-screen max-w-full overflow-x-hidden">
      <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 text-center text-gray-800">Product Catalog</h1>
      {!selectedImage && (
        <p className="text-center text-gray-600 mb-4 max-w-2xl mx-auto">
          Choose a product, then pick a ready-made design. No account needed — just an email and a shipping address.
        </p>
      )}

      {selectedImage && (
        <div
          className="mb-4 max-w-4xl mx-auto rounded-xl border border-sky-200 bg-sky-50/90 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 shadow-sm"
          role="status"
        >
          {designBannerThumb && (
            <img
              src={designBannerThumb}
              alt=""
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover border border-white shadow-sm flex-shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-sky-900 uppercase tracking-wide">Design selected</p>
            <p className="font-medium text-gray-900 truncate">{designBannerTitle}</p>
            <p className="text-sm text-gray-600 mt-0.5">Now select a product to place it on.</p>
          </div>
        </div>
      )}

      <div className="mb-6">
        <input
          type="text"
          placeholder="Search products..."
          className="w-full max-w-xl mx-auto block p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition duration-200"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-56 flex-shrink-0">
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
            >
              <Filter size={18} />
              Filter by category
            </button>
          </div>
          <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block bg-white rounded-lg border border-gray-200 p-4 shadow-sm space-y-6`}>
            <div className="relative" ref={categoryWrapRef}>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3" id="category-heading">
                Category
              </h3>
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={categoryOpen}
                aria-labelledby="category-heading"
                onClick={() => setCategoryOpen((o) => !o)}
                className="flex w-full items-center justify-between gap-2 text-left px-3 py-2 rounded-lg font-medium border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <span className="truncate">{selectedProductType || 'All categories'}</span>
                <ChevronDown
                  size={18}
                  className={`flex-shrink-0 text-gray-500 transition-transform ${categoryOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
              {categoryOpen && (
                <div className="absolute left-0 right-0 z-20 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      ref={categorySearchInputRef}
                      type="search"
                      placeholder="Search categories..."
                      value={categoryQuery}
                      onChange={(e) => setCategoryQuery(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      autoComplete="off"
                    />
                  </div>
                  <ul className="max-h-60 overflow-y-auto py-1" role="listbox" aria-label="Product categories">
                    {filteredCategoryOptions.length === 0 ? (
                      <li className="px-3 py-2 text-sm text-gray-500" role="presentation">
                        No matches
                      </li>
                    ) : (
                      filteredCategoryOptions.map((type) => {
                        const isActive = (type === 'All' && !selectedProductType) || selectedProductType === type;
                        return (
                          <li key={type} role="presentation">
                            <button
                              type="button"
                              role="option"
                              aria-selected={isActive}
                              onClick={() => {
                                setSelectedProductType(type === 'All' ? '' : type);
                                setCategoryOpen(false);
                                setCategoryQuery('');
                                setSidebarOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors ${
                                isActive ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {type}
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </div>
              )}
            </div>
            <div className="relative" ref={shipsToWrapRef}>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3" id="ships-to-heading">
                Ships to
              </h3>
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={shipsToOpen}
                aria-labelledby="ships-to-heading"
                onClick={() => setShipsToOpen((o) => !o)}
                className="flex w-full items-center justify-between gap-2 text-left px-3 py-2 rounded-lg font-medium border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <span className="truncate">{selectedShipping || 'All destinations'}</span>
                <ChevronDown
                  size={18}
                  className={`flex-shrink-0 text-gray-500 transition-transform ${shipsToOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
              {shipsToOpen && (
                <div className="absolute left-0 right-0 z-20 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      ref={shipsToSearchInputRef}
                      type="search"
                      placeholder="Search destinations..."
                      value={shipsToQuery}
                      onChange={(e) => setShipsToQuery(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      autoComplete="off"
                    />
                  </div>
                  <ul className="max-h-60 overflow-y-auto py-1" role="listbox" aria-label="Shipping destinations">
                    {filteredShippingOptions.length === 0 ? (
                      <li className="px-3 py-2 text-sm text-gray-500" role="presentation">
                        No matches
                      </li>
                    ) : (
                      filteredShippingOptions.map((loc) => {
                        const isActive = (loc === 'All' && !selectedShipping) || selectedShipping === loc;
                        return (
                          <li key={loc} role="presentation">
                            <button
                              type="button"
                              role="option"
                              aria-selected={isActive}
                              onClick={() => {
                                userPickedShippingRef.current = true;
                                setSelectedShipping(loc === 'All' ? '' : loc);
                                setShipsToOpen(false);
                                setShipsToQuery('');
                                setSidebarOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors ${
                                isActive ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {loc}
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </div>
              )}
            </div>
            <div className="relative" ref={printLocWrapRef}>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3" id="print-loc-heading">
                Print location
              </h3>
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={printLocOpen}
                aria-labelledby="print-loc-heading"
                onClick={() => setPrintLocOpen((o) => !o)}
                className="flex w-full items-center justify-between gap-2 text-left px-3 py-2 rounded-lg font-medium border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <span className="truncate">{selectedPrintLocationLabel}</span>
                <ChevronDown
                  size={18}
                  className={`flex-shrink-0 text-gray-500 transition-transform ${printLocOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
              {printLocOpen && (
                <div className="absolute left-0 right-0 z-20 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      ref={printLocSearchInputRef}
                      type="search"
                      placeholder="Search placements..."
                      value={printLocQuery}
                      onChange={(e) => setPrintLocQuery(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      autoComplete="off"
                    />
                  </div>
                  <ul className="max-h-60 overflow-y-auto py-1" role="listbox" aria-label="Print locations">
                    {filteredPrintLocationOptions.length === 0 ? (
                      <li className="px-3 py-2 text-sm text-gray-500" role="presentation">
                        No matches
                      </li>
                    ) : (
                      filteredPrintLocationOptions.map((opt) => {
                        const isActive =
                          (!opt.type && !selectedPrintLocation) || selectedPrintLocation === opt.type;
                        return (
                          <li key={opt.type || 'all'} role="presentation">
                            <button
                              type="button"
                              role="option"
                              aria-selected={isActive}
                              onClick={() => {
                                setSelectedPrintLocation(opt.type || '');
                                setPrintLocOpen(false);
                                setPrintLocQuery('');
                                setSidebarOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors ${
                                isActive ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {opt.title}
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </div>
              )}
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="w-full text-left px-3 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 border border-gray-200"
              >
                Clear all filters
              </button>
            )}
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-sm text-gray-600">Active filters:</span>
              {debouncedSearch.trim() && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  Search: {debouncedSearch.trim()}
                  <button type="button" onClick={() => setSearchTerm('')} className="hover:bg-primary/20 rounded-full p-0.5" aria-label="Clear search">
                    {' '}
                    <X size={14} />
                  </button>
                </span>
              )}
              {selectedProductType && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  {selectedProductType}
                  <button type="button" onClick={clearFilter} className="hover:bg-primary/20 rounded-full p-0.5" aria-label="Clear category">
                    {' '}
                    <X size={14} />
                  </button>
                </span>
              )}
              {selectedShipping && selectedShipping !== 'All' && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  Ships to: {selectedShipping}
                  <button
                    type="button"
                    onClick={() => {
                      userPickedShippingRef.current = true;
                      setSelectedShipping('');
                    }}
                    className="hover:bg-primary/20 rounded-full p-0.5"
                    aria-label="Clear shipping"
                  >
                    {' '}
                    <X size={14} />
                  </button>
                </span>
              )}
              {selectedPrintLocation && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  Print: {printLocationOptions.find((o) => o.type === selectedPrintLocation)?.title || selectedPrintLocation}
                  <button type="button" onClick={() => setSelectedPrintLocation('')} className="hover:bg-primary/20 rounded-full p-0.5" aria-label="Clear print location">
                    {' '}
                    <X size={14} />
                  </button>
                </span>
              )}
              <button type="button" onClick={clearAllFilters} className="text-sm text-gray-500 hover:text-gray-700 underline">
                Clear all
              </button>
            </div>
          )}

          {loading ? (
            <p className="text-center text-gray-600 py-12">Loading products...</p>
          ) : error ? (
            <p className="text-center text-red-500 py-12">{error}</p>
          ) : productsCatalog.length === 0 ? (
            <div className="text-center text-gray-600 py-12 space-y-3">
              <p>No products match your filters.</p>
              <p className="text-sm text-gray-500">Try clearing filters or widening category / Ships to / search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-6 tour-step-9">
              {productsCatalog.map((product) => {
                const selectable = catalogProductIsSelectable(product);
                return (
                <div
                  key={catalogItemKey(product)}
                  className={`border border-gray-200 rounded-lg shadow-lg overflow-hidden transition-all duration-300 tour-step-10 ${
                    selectable
                      ? 'cursor-pointer hover:shadow-xl'
                      : 'cursor-not-allowed opacity-60 hover:shadow-lg'
                  }`}
                  onClick={() => selectable && handleProductSelect(product)}
                  onKeyDown={(e) => selectable && e.key === 'Enter' && handleProductSelect(product)}
                  role="button"
                  tabIndex={selectable ? 0 : -1}
                  aria-disabled={!selectable}
                >
                  <div className="relative bg-gray-50">
                    {!selectable && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 px-2 text-center">
                        <span className="text-sm font-semibold text-red-700">
                          {product.is_discontinued ? 'Discontinued' : 'Unavailable'}
                        </span>
                      </div>
                    )}
                    <img src={product.image} alt={product.name} className="w-full h-64 object-contain" />
                  </div>
                  <div className="p-4">
                    <h2 className="text-lg font-semibold mb-1 text-gray-800">{product.name}</h2>
                    <p className="text-gray-600 text-sm">Estimated Price: ${product.selling_price ? product.selling_price.toFixed(2) : 'N/A'}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Ships to:{' '}
                      {product.coverage_locations?.length > 0
                        ? product.coverage_locations.slice(0, 3).join(', ') + (product.coverage_locations.length > 3 ? '...' : '')
                        : 'Worldwide'}
                    </p>
                    <p
                      className="text-xs text-gray-500 mt-1"
                      title={product.supported_print_placements?.length > 0 ? product.supported_print_placements.map((p) => p.title).join(', ') : 'Front'}
                    >
                      Print areas:{' '}
                      {(() => {
                        const placements = product.supported_print_placements || [];
                        if (placements.length === 0) return 'Front';
                        const maxShow = 3;
                        const shown = placements.slice(0, maxShow).map((p) => p.title).join(', ');
                        const rest = placements.length - maxShow;
                        return rest > 0 ? `${shown} +${rest} more` : shown;
                      })()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Fulfillment: {product.avg_fulfillment_time ? `${product.avg_fulfillment_time} business days` : '5-7 business days'}</p>
                    <p className="text-xs text-gray-500 mt-1">Availability: {getAvailabilityStatus(product.availability_statuses)}</p>
                  </div>
                </div>
                );
              })}
            </div>
          )}
          {!loading && !error && productsCatalog.length < catalogTotal && (
            <div className="flex justify-center mt-8">
              <button
                type="button"
                onClick={loadMoreCatalog}
                disabled={loadingMore}
                className="px-6 py-3 rounded-lg bg-primary text-white font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? 'Loading…' : 'Load more products'}
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProductCatalogPage;
